/**
 * Functions, types for 3x3 matrices that are commonly used for transforming 2d geometry
 * @module
 */
import degreeToRadian from "common/utils/degreeToRadian";
import { DIST_EPSILON } from "./core";
import { line2Tangent } from "./line2";
import { ray2DotProject } from "./ray2";
import { solveLinearEquationSystem } from "./solveLinearEquationSystem";
import type { Vec2 } from "./vec2";
import { vec2AngleBetween, vec2Dist, vec2Len, vec2Result } from "./vec2";

// Similar to three.js, our matrices are specified in column-major order.

export interface Mat3 {
  [position: number]: number;
}

// TODO: rename this and `mat4Ident` to `mat3Identity` and `mat4Identity` respectively.
export function mat3Ident(dst?: Mat3): Mat3 {
  dst = dst || new Array<number>(9);

  dst[0] = 1;
  dst[1] = 0;
  dst[2] = 0;

  dst[3] = 0;
  dst[4] = 1;
  dst[5] = 0;

  dst[6] = 0;
  dst[7] = 0;
  dst[8] = 1;
  return dst;
}

export function mat3Rotate(rad: number, dst?: Mat3): Mat3 {
  dst = dst || new Array<number>(9);

  const s = Math.sin(rad);
  const c = Math.cos(rad);

  dst[0] = c;
  dst[1] = s;
  dst[2] = 0;

  dst[3] = -s;
  dst[4] = c;
  dst[5] = 0;

  dst[6] = 0;
  dst[7] = 0;
  dst[8] = 1;

  return dst;
}

export function mat3RotateDegrees(degrees: number, dst?: Mat3): Mat3 {
  return mat3Rotate(degreeToRadian(degrees), dst);
}

export function mat3RotateByCenterStartEndPoint(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
): Mat3 {
  const [tx0, ty0] = line2Tangent(ax, ay, bx, by);
  const [tx1, ty1] = line2Tangent(ax, ay, cx, cy);
  // The tangent can be NaN if the line is a point.
  if (isNaN(tx0) || isNaN(ty0) || isNaN(tx1) || isNaN(ty1)) {
    return mat3Ident();
  }

  const radians = vec2AngleBetween(tx0, ty0, tx1, ty1);
  const translate1 = mat3Translate(ax, ay);
  const rotate1 = mat3Rotate(radians);
  const translate2 = mat3Translate(-ax, -ay);

  return mat3Mult(translate1, mat3Mult(rotate1, translate2));
}

export function mat3ExtractRotationMatrixFrom(m: Mat3, dst?: Mat3): Mat3 | null {
  dst = dst || new Array<number>(9);

  const scales: Vec2 = mat3ExtractScaleVectorFrom(m);
  if (Math.abs(scales[0]) < DIST_EPSILON || Math.abs(scales[1]) < DIST_EPSILON) {
    return null;
  }

  dst[0] = m[0] / scales[0];
  dst[1] = m[1] / scales[0];
  dst[2] = 0;

  dst[3] = m[3] / scales[1];
  dst[4] = m[4] / scales[1];
  dst[5] = 0;

  dst[6] = 0;
  dst[7] = 0;
  dst[8] = 1;

  return dst;
}

export function mat3ExtractRotationAngleFrom(m: Mat3, scales?: Vec2): number | null {
  if (!scales) {
    scales = mat3ExtractScaleVectorFrom(m);
  }
  if (Math.abs(scales[0]) < DIST_EPSILON || Math.abs(scales[1]) < DIST_EPSILON) {
    console.error(`scales[0]=${scales[0]}, scales[1]=${scales[1]}`);
    return null;
  }

  const c = (m[0] / scales[0] + m[4] / scales[1]) / 2;
  const s = (-m[1] / scales[0] + m[3] / scales[1]) / 2;
  if (Math.abs(c) > 1 || Math.abs(s) > 1) {
    // Should never happen, but to be on the safe side.
    return null;
  }

  // For the reason to multiply by the sign of scales[1], see the comment in `mat3ExtractScaleVectorFrom`.
  return -Math.atan2(s, c) * Math.sign(scales[1]);
}

export function mat3Scale(x: number, y: number, dst?: Mat3): Mat3 {
  dst = dst || new Array<number>(9);

  dst[0] = x;
  dst[1] = 0;
  dst[2] = 0;

  dst[3] = 0;
  dst[4] = y;
  dst[5] = 0;

  dst[6] = 0;
  dst[7] = 0;
  dst[8] = 1;
  return dst;
}

export function mat3ExtractScaleVectorFrom(m: Mat3, dst?: Vec2): Vec2 {
  dst = dst || new Array<number>(2);

  // Extracting the scales and rotation angle is an ambiguous problem if we allow negative scaling
  // factors (flipping). E.g. flipping in both x and y direction and rotation by an `alpha` is
  // the same thing as not flipping and rotation by `180 + alpha`. The sign of the diagonal elements
  // gives us the mod2 number of how many flips happened. If it's positive then either both x and y
  // are flipped or none of them. If it's negative then either x or y is flipped but not both.
  // Since in Higharc code the typical use case is to have a single y flip, we'll assume that.
  const signValue = m[0] * m[4];
  dst[0] = vec2Len(m[0], m[1]);
  dst[1] = vec2Len(m[3], m[4]) * Math.sign(signValue || 1);

  return dst;
}

export function mat3Translate(x: number, y: number, dst?: Mat3): Mat3 {
  dst = dst || new Array<number>(9);

  dst[0] = 1;
  dst[1] = 0;
  dst[2] = 0;
  dst[3] = 0;
  dst[4] = 1;
  dst[5] = 0;
  dst[6] = x;
  dst[7] = y;
  dst[8] = 1;
  return dst;
}

export function mat3ExtractTranslationVectorFrom(m: Mat3, dst?: Vec2): Vec2 {
  dst = dst || new Array<number>(2);

  dst[0] = m[6];
  dst[1] = m[7];

  return dst;
}

export function mat3NegateX(dst: Mat3): Mat3 {
  dst[6] = -dst[6];
  return dst;
}

export function mat3ReflectAlongAxis(ax: number, ay: number, bx: number, by: number): Mat3 {
  const radians = vec2AngleBetween(1, 0, bx - ax, by - ay);

  const t0 = mat3Translate(ax, ay);
  const r = mat3Reflect(radians);
  const t1 = mat3Translate(-ax, -ay);

  return mat3Mult(t0, mat3Mult(r, t1));
}

export function mat3Reflect(radians: number, dst?: Mat3): Mat3 {
  dst = dst || new Array<number>(9);

  const c = Math.cos(2 * radians);
  const s = Math.sin(2 * radians);

  dst[0] = c;
  dst[1] = s;
  dst[2] = 0;

  dst[3] = s;
  dst[4] = -c;
  dst[5] = 0;

  dst[6] = 0;
  dst[7] = 0;
  dst[8] = 1;

  return dst;
}

export function mat3Mult(a: Mat3, b: Mat3, dst?: Mat3): Mat3 {
  dst = dst || new Array<number>(9);

  const a00 = a[0];
  const a01 = a[1];
  const a02 = a[2];
  const a10 = a[3];
  const a11 = a[4];
  const a12 = a[5];
  const a20 = a[6];
  const a21 = a[7];
  const a22 = a[8];

  const b00 = b[0];
  const b01 = b[1];
  const b02 = b[2];
  const b10 = b[3];
  const b11 = b[4];
  const b12 = b[5];
  const b20 = b[6];
  const b21 = b[7];
  const b22 = b[8];

  dst[0] = b00 * a00 + b01 * a10 + b02 * a20;
  dst[1] = b00 * a01 + b01 * a11 + b02 * a21;
  dst[2] = b00 * a02 + b01 * a12 + b02 * a22;

  dst[3] = b10 * a00 + b11 * a10 + b12 * a20;
  dst[4] = b10 * a01 + b11 * a11 + b12 * a21;
  dst[5] = b10 * a02 + b11 * a12 + b12 * a22;

  dst[6] = b20 * a00 + b21 * a10 + b22 * a20;
  dst[7] = b20 * a01 + b21 * a11 + b22 * a21;
  dst[8] = b20 * a02 + b21 * a12 + b22 * a22;
  return dst;
}

export function mat3Dot(m: Mat3, x: number, y: number): Vec2 {
  vec2Result[0] = m[0] * x + m[3] * y + m[6];
  vec2Result[1] = m[1] * x + m[4] * y + m[7];
  return vec2Result;
}

export function mat3DotNoTranslate(m: Mat3, x: number, y: number): Vec2 {
  vec2Result[0] = m[0] * x + m[3] * y;
  vec2Result[1] = m[1] * x + m[4] * y;
  return vec2Result;
}

export function mat3DotHomogeneous(m: Mat3, x: number, y: number, z: number = 1): Vec2 {
  const w = m[2] * x + m[5] * y + m[8] * z;
  vec2Result[0] = (m[0] * x + m[3] * y + m[6] * z) / w;
  vec2Result[1] = (m[1] * x + m[4] * y + m[7] * z) / w;
  return vec2Result;
}

export function mat3Inverse(m: Mat3): Mat3 {
  const invdet = 1 / mat3Determinant(m);

  const dst = new Array<number>(9);
  dst[0] = (m[4] * m[8] - m[7] * m[5]) * invdet;
  dst[1] = (m[2] * m[7] - m[1] * m[8]) * invdet;
  dst[2] = (m[1] * m[5] - m[2] * m[4]) * invdet;
  dst[3] = (m[5] * m[6] - m[3] * m[8]) * invdet;
  dst[4] = (m[0] * m[8] - m[2] * m[6]) * invdet;
  dst[5] = (m[3] * m[2] - m[0] * m[5]) * invdet;
  dst[6] = (m[3] * m[7] - m[6] * m[4]) * invdet;
  dst[7] = (m[6] * m[1] - m[0] * m[7]) * invdet;
  dst[8] = (m[0] * m[4] - m[3] * m[1]) * invdet;

  return dst;
}

export function mat3Determinant(m: Mat3): number {
  return m[0] * (m[4] * m[8] - m[7] * m[5]) - m[1] * (m[3] * m[8] - m[5] * m[6]) + m[2] * (m[3] * m[7] - m[4] * m[6]);
}

export function mat3ScaleByThreePoints(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  tolerance: number = DIST_EPSILON,
): Mat3 {
  const d0 = vec2Dist(ax, ay, bx, by);

  // if two points are the same, return identity matrix
  if (d0 < tolerance) {
    return [1, 0, 0, 0, 1, 0, 0, 0, 1];
  }

  const [tx, ty] = line2Tangent(ax, ay, bx, by);

  const d1 = ray2DotProject(ax, ay, tx, ty, cx, cy);

  const scale = d1 / d0;

  return mat3ScaleByCenterPointScale(ax, ay, scale);
}

export function mat3ScaleByTwoPointsLength(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  length: number,
  tolerance: number = DIST_EPSILON,
): Mat3 {
  const d0 = vec2Dist(ax, ay, bx, by);

  if (d0 < tolerance) {
    return [1, 0, 0, 0, 1, 0, 0, 0, 1];
  }

  const d1 = length;

  const scale = d1 / d0;

  return mat3ScaleByCenterPointScale(ax, ay, scale);
}

export function mat3Transpose(dst: Mat3): Mat3 {
  const src = mat3Copy(dst);

  dst[1] = src[3];
  dst[2] = src[6];

  dst[3] = src[1];
  dst[5] = src[7];

  dst[6] = src[2];
  dst[7] = src[5];

  return dst;
}

export function mat3Copy(m: Mat3, dst?: Mat3): Mat3 {
  dst = dst || new Array<number>(9);

  dst[0] = m[0];
  dst[1] = m[1];
  dst[2] = m[2];

  dst[3] = m[3];
  dst[4] = m[4];
  dst[5] = m[5];

  dst[6] = m[6];
  dst[7] = m[7];
  dst[8] = m[8];

  return dst;
}

export function mat3ScaleByCenterPointScale(ax: number, ay: number, scale: number): Mat3 {
  const translate1 = mat3Translate(ax, ay);
  const scaling = mat3Scale(scale, scale);
  const translate2 = mat3Translate(-ax, -ay);

  return mat3Mult(translate1, mat3Mult(scaling, translate2));
}

export function mat3RotateAroundPoint(ax: number, ay: number, radians: number): Mat3 {
  const translate1 = mat3Translate(ax, ay);
  const scaling = mat3Rotate(radians);
  const translate2 = mat3Translate(-ax, -ay);

  return mat3Mult(translate1, mat3Mult(scaling, translate2));
}

export function mat3StretchByThreePoints(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  tolerance: number = DIST_EPSILON,
): Mat3 {
  const d0 = vec2Dist(ax, ay, bx, by);

  // if two points are the same, return identity matrix
  if (d0 < tolerance) {
    return [1, 0, 0, 0, 1, 0, 0, 0, 1];
  }

  const [tx, ty] = line2Tangent(ax, ay, bx, by);

  const d1 = ray2DotProject(ax, ay, tx, ty, cx, cy);
  const scale = d1 / d0;
  const radians = vec2AngleBetween(1, 0, tx, ty);
  const translate1 = mat3Translate(ax, ay);
  const rotate1 = mat3Rotate(radians);
  const scaling = mat3Scale(scale, 1);
  const rotate2 = mat3Rotate(-radians);
  const translate2 = mat3Translate(-ax, -ay);

  return mat3Mult(translate1, mat3Mult(rotate1, mat3Mult(scaling, mat3Mult(rotate2, translate2))));
}

export function mat3StretchByTwoPointsLength(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  d1: number,
  tolerance: number = DIST_EPSILON,
): Mat3 {
  const d0 = vec2Dist(ax, ay, bx, by);

  if (d0 < tolerance) {
    return [1, 0, 0, 0, 1, 0, 0, 0, 1];
  }

  const [tx, ty] = line2Tangent(ax, ay, bx, by);

  const scale = d1 / d0;
  const radians = vec2AngleBetween(1, 0, tx, ty);
  const translate1 = mat3Translate(ax, ay);
  const rotate1 = mat3Rotate(radians);
  const scaling = mat3Scale(scale, 1);
  const rotate2 = mat3Rotate(-radians);
  const translate2 = mat3Translate(-ax, -ay);

  return mat3Mult(translate1, mat3Mult(rotate1, mat3Mult(scaling, mat3Mult(rotate2, translate2))));
}

// Map two rectangular regions (src -> dst) using linear interpolation in each dimension:
//
//  dst = dst_start +  (dst_end - dst_start) * (src - src_start)
//                     ---------------------
//                     (src_end - src_start)
//
// .    = dst_start + k * (src - src_start)
//
//      = (dst_start - k * src_start) + k * src
export function mat3RectLinearMapping(
  srcX0: number,
  srcY0: number,
  srcX1: number,
  srcY1: number,
  dstX0: number,
  dstY0: number,
  dstX1: number,
  dstY1: number,
): Mat3 {
  const m = new Array<number>(9);

  const kX = (dstX1 - dstX0) / (srcX1 - srcX0);
  const kY = (dstY1 - dstY0) / (srcY1 - srcY0);

  m[0] = kX;
  m[1] = 0;
  m[2] = 0;
  m[3] = 0;
  m[4] = kY;
  m[5] = 0;
  m[6] = dstX0 - kX * srcX0;
  m[7] = dstY0 - kY * srcY0;
  m[8] = 1;

  return m;
}

export function mat3IsMirroringAffineTransform(m: Mat3): boolean {
  // Sign of the determinant of the linear part of the transform tells if the matrix is mirroring.
  return m[0] * m[4] - m[1] * m[3] < 0;
}

/**
 * Solve an affine transform based on a set of source points and the corresponding transformed points.
 * @param points Untransformed points
 * @param transformedPoints Images of the points in the transformed space
 * @returns An affine transform matrix.
 */
export function mat3SolveAffineTransformFromPoints(points: number[], transformedPoints: number[]): Mat3 {
  // The notation in this comment uses ; to separate matrix rows. So [x; y] is a column vector.
  //
  // Affine transform [x'; y'] = A * [x; y] can be expressed also like this,
  // where the parameters in the transformation matrix A are given as a column vector instead:
  //
  // X = [x y 1 0 0 0;
  //      0 0 0 x y 1]
  //
  // a = [a11; a12; a13; a21; a22; a23]
  //
  // [x"; y'] = X * a;
  //
  // stacking the coordinates together we get a linear equation we can solve for "a":
  //
  // [x0"; y0"; x1"; y1"; x2"; y2'] = [X0; X1; X2] * a;

  const X: number[][] = [];
  for (let i = 0; i < points.length / 2; i++) {
    const x = points[i * 2];
    const y = points[i * 2 + 1];
    X.push([x, y, 1, 0, 0, 0]);
    X.push([0, 0, 0, x, y, 1]);
  }

  const a = solveLinearEquationSystem(X, transformedPoints);

  return [a[0], a[3], 0, a[1], a[4], 0, a[2], a[5], 1];
}

/**
 *
 * @returns The affine transform matrix that maps the line segment [a, b] to [c, d].
 */
export function mat3LineSegmentAffineTransform(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
  supressLengthScaling: boolean = false,
  supressTranslation: boolean = false,
  supressRotation: boolean = false,
  tolerance: number = DIST_EPSILON,
): Mat3 {
  const lengthA = vec2Dist(ax, ay, bx, by);
  const lengthB = vec2Dist(cx, cy, dx, dy);

  if (lengthA < tolerance || lengthB < tolerance || (supressLengthScaling && supressRotation && supressTranslation)) {
    return mat3Ident();
  }

  const scaling = supressLengthScaling ? 1 : lengthB / lengthA;

  const translationX = supressTranslation ? 0 : cx - ax;
  const translationY = supressTranslation ? 0 : cy - ay;

  const rotation = supressRotation ? 0 : vec2AngleBetween(cx - dx, cy - dy, ax - bx, ay - by);
  const cosTheta = Math.cos(rotation);
  const sinTheta = Math.sin(rotation);

  //Form the matrix with rotation, scaling and translation
  const matrix = new Array<number>(9);

  matrix[0] = scaling * cosTheta;
  matrix[1] = -scaling * sinTheta;
  matrix[2] = translationX;

  matrix[3] = scaling * sinTheta;
  matrix[4] = scaling * cosTheta;
  matrix[5] = translationY;

  matrix[6] = 0;
  matrix[7] = 0;
  matrix[8] = 1;

  return matrix;
}

export function mat3CSSRotate(angleDegrees: number, cx = 0, cy = 0, dst?: Mat3): Mat3 {
  const angleRads = degreeToRadian(angleDegrees);
  return mat3CSSRotateRads(angleRads, cx, cy, dst);
}

export function mat3CSSRotateRads(angleRads: number, cx = 0, cy = 0, dst?: Mat3): Mat3 {
  if (cx === 0 && cy === 0) {
    return mat3Rotate(angleRads, dst);
  }

  // Rotate around center (cx, cy)
  const tempTransform1 = mat3Translate(-cx, -cy);
  const tempTransform2 = mat3Rotate(angleRads);
  const tempTransform3 = mat3Mult(tempTransform2, tempTransform1);
  mat3Translate(cx, cy, tempTransform1);
  return mat3Mult(tempTransform1, tempTransform3, dst);
}

export function mat3Reduce(transforms: Mat3[]): Mat3 {
  // This matrix reduce is based on "parseNodeTransform" in svgParser.ts:
  const finalTransform = mat3Ident();
  for (let i = transforms.length - 1; i >= 0; --i) {
    const parsedTransform = transforms[i];
    mat3Mult(parsedTransform, finalTransform, finalTransform);
  }
  return finalTransform;
}
